import { useMemo } from 'react';
import type { NavbarVariant } from '@packages/ui/layout';
import { useGetCurrentTenantInfo } from '@/hooks';
import useInstance from '@/hooks/custom-hooks/useInstance';
import usePermissionCheck from '@/hooks/custom-hooks/usePermissionCheck';

export interface TenantInfoResult {
  tenantName: string | null;
  tenantType: string | null;
  companyId: string | null;
  isSandbox: boolean;
  isSwitched: boolean;
  instanceName: string | null;
  variant: NavbarVariant;
  canSwitchCompany: boolean;
  canSwitchSandbox: boolean;
  canSwitch: boolean;
  isLoading: boolean;
}

interface UseTenantInfoOptions {
  handleCustomerRequest: <T>(request: Promise<T>) => Promise<T | undefined>;
}

/**
 * Customer-only hook that encapsulates tenant/sandbox/instance state.
 * Derives the navbar variant and switch permissions.
 *
 * Must be rendered inside `<AuthGate>` — no auth guard needed.
 */
export function useTenantInfo({ handleCustomerRequest }: UseTenantInfoOptions): TenantInfoResult {
  const { data: currentTenant, isLoading: isTenantLoading } =
    useGetCurrentTenantInfo(handleCustomerRequest);

  const { isSwitched, getInstance } = useInstance();

  const switched = Boolean(isSwitched());
  const instanceInfo = switched ? getInstance() : { instanceName: null, instanceId: null };

  const hasSwitchCompanyPermission = usePermissionCheck({
    type: 'View',
    feature: 'Customer',
    permission: 'SwitchCompany',
  });

  const hasSandboxPermission = usePermissionCheck({
    type: 'View',
    feature: 'Customer',
    permission: 'Sandbox',
  });

  const resolvedInstanceName = useMemo(() => {
    return (
      currentTenant?.name ?? '-' // (switched ? (instanceInfo?.instanceName ?? null) : null)
    );
  }, [currentTenant?.name, switched, instanceInfo?.instanceName]);

  const isSandbox = useMemo(() => {
    if (!switched) return false;
    if (currentTenant?.tenantType) return currentTenant.tenantType !== 'Main';
    return false;
    // return instanceInfo?.instanceName !== 'Main';
  }, [switched, currentTenant?.tenantType, instanceInfo?.instanceName]);

  const variant = useMemo((): NavbarVariant => {
    if (isSandbox) return 'sandbox';
    if (switched) return 'switched';
    return 'default';
  }, [isSandbox, switched]);

  return {
    tenantName: currentTenant?.companyName ?? null,
    tenantType: currentTenant?.tenantType ?? null,
    companyId: currentTenant?.companyId ?? null,
    isSandbox,
    isSwitched: switched,
    instanceName: resolvedInstanceName,
    variant,
    canSwitchCompany: hasSwitchCompanyPermission,
    canSwitchSandbox: hasSandboxPermission,
    canSwitch: hasSwitchCompanyPermission || hasSandboxPermission,
    isLoading: isTenantLoading,
  };
}
